import React, {createContext,ReactNode,Dispatch,SetStateAction,useCallback,useContext,useMemo,useState,startTransition, useEffect,
} from "react";
import { AppContext } from "./AppContext";
import { baseDays, FilterValue, formatDateToYMD, isBetween, Restaurant, typeSchedule, typeSpecialDate } from "../components/types/types";
import {  isTypedRestaurantFilter, restaurantTypeMatchesFilter } from "../components/types/restaurantTypes";

const normalizeSearch = (value: string) => {
    return value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
};
const normalizeCitySearch = (value: string) => {
    return normalizeSearch(value)
        .replace(/[-']/g, " ")
        .replace(/\s+/g, " ")
        .trim();
};
export interface SearchMiniResto {
    idrestaurant: number; 
    id_url:string,
    name:string,
    devanture:string,
    note: number; 
    type:string[],
    type_display:string,
    filters?:string[],
    schedule: typeSchedule;
    special_date?: typeSpecialDate;
    cuisines:string[],
    address:[string,string],
    visiblecity?:string,
    price_range?:[number,number],
    location?: {
        type: string;
        coordinates: [number, number]; 
    };
    neighborhood?: string;
}
export interface SearchMiniRestoIndexed extends SearchMiniResto {
  _nameNorm: string;
  _addressCityNorm: string;
  _visibleCityNorm: string;
}
export const indexMiniRestos = (
  restos: SearchMiniResto[]
): SearchMiniRestoIndexed[] => {
  return restos.map(resto => ({
    ...resto,
    _nameNorm: normalizeSearch(resto.name),
    _addressCityNorm: normalizeCitySearch(resto.address?.[1] ?? ""),
    _visibleCityNorm: normalizeCitySearch(resto.visiblecity ?? ""),
  }));
};



const toMinutes = (h: unknown): number => {
  if (typeof h !== "string") {
    return NaN;
  }
  const m = h.trim().match(/^(\d{1,2})h(\d{2})$/);
  if (!m) return NaN;

  const hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  return hh * 60 + mm;
};

const nowMinutes = (): number => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
};
const getRangesForDate = (restaurant: Restaurant, date: Date): Array<[number, number]> => {
    const ymd = formatDateToYMD(date);
    const dayKey = baseDays[date.getDay()];
    const special = restaurant.special_date?.[ymd];
    const normalizeRange = (startRaw: unknown,endRaw: unknown): [number, number] | null => {
        const start = toMinutes(startRaw);
        let end = toMinutes(endRaw);
        if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
        if (end <= start) end += 24 * 60;
        return [start, end];
    };
    if (special) {
        const isOpen = special?.[0];
        if (isOpen !== true) return [];

        const slots = special?.[1];
        if (!Array.isArray(slots) || !Array.isArray(slots[0])) return [];

        return slots
            .map((r: any) => normalizeRange(r?.[0], r?.[1]))
            .filter((r): r is [number, number] => r !== null);
    }
    const sched = restaurant.schedule?.[dayKey];
    if (!Array.isArray(sched)) return [];
    return sched
        .map((r) => normalizeRange(r[0], r[1]))
        .filter((r): r is [number, number] => r !== null);
};
const addDays = (date: Date, days: number): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};
const isStillValidForServiceFilter = (restaurant: Restaurant,service: "lunch" | "dinner" | "lunch_tomorrow" | "dinner_tomorrow" | "now"): boolean => {
    const currentDate = new Date();
    const date = service === "lunch_tomorrow" || service === "dinner_tomorrow" ? addDays(currentDate, 1) : currentDate;

    const ranges = getRangesForDate(restaurant, date);
    if (!ranges.length) return false;
    if (service === "now") {
        const current = nowMinutes();
        const tolerance = 15;

        return ranges.some(([s, e]) => {
            let adjustedStart = s - tolerance;
            if (adjustedStart < 0) adjustedStart += 24 * 60;
            if (e >= s) return adjustedStart <= current && current <= e;
            return current >= adjustedStart || current <= e;
        });
    }
    const target = (service === "lunch" || service === "lunch_tomorrow")  ? toMinutes("12h30") : toMinutes("20h00");
    const chosen = ranges.find(([s, e]) => s <= target && target <= e);
    if (!chosen) return false;
    if (service === "lunch" || service === "dinner") {
        const current = nowMinutes();
        return current <= chosen[1];
    }

    return true;
};
const isCuisineFilter = (filter: string, restaurants: Restaurant[]) => {
    return restaurants.some(resto => resto.cuisines?.includes(filter));
};
export const filterRestaurantsWithFilters = (
    restaurants: Restaurant[],
    filters: FilterValue[]
): Restaurant[] => {
    if (filters.length === 0) return restaurants;
    const filterCuisines = filters.filter((filter): filter is string =>
        typeof filter === "string" && isCuisineFilter(filter, restaurants)
    );
    const filterTypes = filters.filter((filter): filter is string => {
        return typeof filter === "string" && isTypedRestaurantFilter(filter);
    });
    const filteredByCuisine = restaurants.filter(restaurant => 
        filterCuisines.some(cuisine => restaurant.cuisines.includes(cuisine as string))
    );
    const filteredByType = restaurants.filter(restaurant => 
        filterTypes.some(type =>
            restaurant.type.some(rawType => 
                restaurantTypeMatchesFilter(rawType, type)
            )
        )
    );
    const uniqueRestaurants = [...restaurants,...filteredByCuisine,...filteredByType].filter((restaurant, index, self) => 
        index === self.findIndex((r) => r.idrestaurant === restaurant.idrestaurant )
    );

    return uniqueRestaurants.filter(restaurant => {
            let countType = 0;
            const countCuisine = restaurant.cuisines.some((cuisine) =>filterCuisines.includes(cuisine)) ? 1 : 0;
            if (
                filterTypes.some(type =>
                    restaurant.type.some(rawType => 
                        restaurantTypeMatchesFilter(rawType, type)
            ))) countType += 1;
            return filters.every(filter => {
                if (typeof filter === 'string') {
                    if(isCuisineFilter(filter, restaurants)) {          
                        if(countCuisine>0) return true                
                    }
                    if(isTypedRestaurantFilter(filter)) {          
                        if(countType>0) return true                
                    }
                    if(['Ouvert ce midi', 'Ouvert ce soir', 'Ouvert demain midi', 'Ouvert demain soir','Ouvert maintenant', 'À proximité'].includes(filter)) {
                        if('Ouvert ce midi' === filter) {
                            return isStillValidForServiceFilter(restaurant, "lunch");
                        }
                        if ('Ouvert ce soir' === filter) {
                            return isStillValidForServiceFilter(restaurant, "dinner");
                        }
                        if('Ouvert demain midi' === filter) {
                            return isStillValidForServiceFilter(restaurant, "lunch_tomorrow");
                        }
                        if ('Ouvert demain soir' === filter) {
                            return isStillValidForServiceFilter(restaurant, "dinner_tomorrow");
                        }
                        if('Ouvert maintenant' === filter) {
                            return isStillValidForServiceFilter(restaurant, "now");
                        }
                        return true
                    }
                    return (
                        (['5 étoiles', '4 étoiles', '3 étoiles'].includes(filter) &&
                            restaurant.note >= parseFloat(filter[0])) ||
                                (restaurant.filters?.includes(filter))
                    );
                }
                else if (Array.isArray(filter) ) {
                    if (!restaurant.price_range) return false;
                    const [filterMin, filterMax] = filter;
                    const [restaurantMin, restaurantMax] = restaurant.price_range;
                    const hasNoMax = filterMax >= 100;
                    return (restaurantMax >= filterMin &&(hasNoMax || restaurantMin <= filterMax));
                }
                return true
                
            })
    });
}
export const getScheduleFilteredList = (
    listResto: Restaurant[],
    hour_selected: string,
    date_selected: Date | null
): Restaurant[] => {
    if(hour_selected !== '') {
        if(date_selected) {
            const formattedDate = formatDateToYMD(date_selected)
            return listResto.filter((elem) => {
                if(elem.special_date) { 
                    if(elem.special_date[formattedDate]) { 
                        if(elem.special_date[formattedDate][0]) {
                            if(Array.isArray(elem.special_date[formattedDate][1])) {
                                if(isBetween(elem.special_date[formattedDate][1][0][0],elem.special_date[formattedDate][1][0][1],hour_selected)) return elem
                                else if(elem.special_date[formattedDate][1].length > 1 && isBetween(elem.special_date[formattedDate][1][1][0],elem.special_date[formattedDate][1][1][1],hour_selected)) return elem
                            }
                            else return 
                        }  
                        else return
                    }
                }
                
                if(Array.isArray(elem.schedule[baseDays[date_selected.getDay()]])) {
                    if(isBetween(elem.schedule[baseDays[date_selected.getDay()]][0][0],elem.schedule[baseDays[date_selected.getDay()]][0][1],hour_selected)) return elem
                    else if(elem.schedule[baseDays[date_selected.getDay()]].length > 1 && isBetween(elem.schedule[baseDays[date_selected.getDay()]][1][0],elem.schedule[baseDays[date_selected.getDay()]][1][1],hour_selected)) return elem
                }
                else return 
                
            })
        }
        else {
            const today = new Date()
            const formattedDate = formatDateToYMD(today)
            return listResto.filter((elem) => {
                if(elem.special_date) { 
                    if(elem.special_date[formattedDate]) { 
                        if(elem.special_date[formattedDate][0]) return elem   
                        else return
                    }
                }   
                if(Array.isArray(elem.schedule[baseDays[today.getDay()]])) {
                    if(isBetween(elem.schedule[baseDays[today.getDay()]][0][0],elem.schedule[baseDays[today.getDay()]][0][1],hour_selected)) return elem
                    else if(elem.schedule[baseDays[today.getDay()]].length > 1 && isBetween(elem.schedule[baseDays[today.getDay()]][1][0],elem.schedule[baseDays[today.getDay()]][1][1],hour_selected)) return elem
                }
                else return 
                
            })
        }
    }
    else if(date_selected) {

        const formattedDate = formatDateToYMD(date_selected)
        return listResto.filter((elem) => {
            if(elem.special_date) { 
                if(elem.special_date[formattedDate]) { 
                    if(elem.special_date[formattedDate][0]) return elem   
                    else return
                }
            }
            if(Array.isArray(elem.schedule[baseDays[date_selected.getDay()]])) return elem
            
        })
    }
    else {
        return listResto;
    }
}
type RestaurantContextType = {
    priceFilter: (min: number, max: number) => void;
    fetchRestaurants:() => Promise<void>,
    fetchResto:boolean,
    setFetchResto:Dispatch<SetStateAction<boolean>>,
    listResto:Restaurant[],
    setListResto:Dispatch<SetStateAction<Restaurant[]>>,
    listRestoHome:Restaurant[],
    setListRestoHome:Dispatch<SetStateAction<Restaurant[]>>,
    listRestoAll: Restaurant[];
    setListRestoAll: Dispatch<SetStateAction<Restaurant[]>>;

    listRestoFiltered: Restaurant[];
    setListRestoFiltered: Dispatch<SetStateAction<Restaurant[]>>;
    listRestoBackup: Restaurant[];
    setListRestoBackup: Dispatch<SetStateAction<Restaurant[]>>;

    miniRestos: SearchMiniRestoIndexed[];
    setMiniRestos: Dispatch<SetStateAction<SearchMiniRestoIndexed[]>>;
    miniRestosLoaded: boolean;
    setMiniRestosLoaded: Dispatch<SetStateAction<boolean>>;
    fetchMiniRestos: () => Promise<void>;
    resetCityRestaurants: () => void;
};

const RestaurantContext = createContext<RestaurantContextType>({
    priceFilter: () => {},
    fetchRestaurants:async () => {},
    fetchResto: false,
    setFetchResto: () => {},
    listResto:[],
    setListResto:() => {},
    listRestoHome:[],
    setListRestoHome:() => {},

    listRestoAll: [],
    setListRestoAll: () => {},

    listRestoFiltered: [],
    setListRestoFiltered: () => {},
    listRestoBackup: [],
    setListRestoBackup: () => {},


    miniRestos: [],
    setMiniRestos: () => {},
    miniRestosLoaded: false,
    setMiniRestosLoaded: () => {},
    fetchMiniRestos: async () => {},
    resetCityRestaurants: () => {},

});


export const RestaurantContextProvider = ({ children }: { children: ReactNode }) => {
    const { setFilterChoose,filterChoose,hour_selected,setHour_selected,date_selected,setDate_selected  } = useContext(AppContext);

    const [fetchResto,setFetchResto] = useState(false)
    const [listResto,setListResto] = useState<Restaurant[]>([])
    const [listRestoHome,setListRestoHome] = useState<Restaurant[]>([])

    const [listRestoAll, setListRestoAll] = useState<Restaurant[]>([]);
    const [listRestoFiltered, setListRestoFiltered] = useState<Restaurant[]>([]);
    const [listRestoBackup, setListRestoBackup] = useState<Restaurant[]>([]);

    const [miniRestos, setMiniRestos] = useState<SearchMiniRestoIndexed[]>([]);
    const [miniRestosLoaded, setMiniRestosLoaded] = useState(false);
    const fetchMiniRestos = useCallback(async () => {
        if (miniRestosLoaded) return;
        try {
            const response = await fetch(`${process.env.REACT_APP_DIRECTION_REQUEST}/api/getSearchData`);
            const data = await response.json();
            if (data.success) {
                const miniRestoList = indexMiniRestos(data.restaurants || []);
                startTransition(() => {
                    setMiniRestos(miniRestoList);
                    setMiniRestosLoaded(true);
                });
            }
        } catch (error) {
            console.error("Erreur chargement search data :", error);
        }
    }, [miniRestosLoaded]);

   const changementSchedule_Date = () => {
        const contextualList = getScheduleFilteredList(listResto,hour_selected,date_selected);
        return filterRestaurantsWithFilters(contextualList,filterChoose);
    };

    const openedFilters:string[] = [
    'Ouvert ce midi',
    'Ouvert ce soir',
    'Ouvert demain midi',
    'Ouvert demain soir',
    'Ouvert maintenant'
    ];
    useEffect(() => {
        if(filterChoose.includes('Ouvert ce midi')) {
            const others = openedFilters.filter(f => f !== 'Ouvert ce midi');
            if (others.some(f => filterChoose.includes(f))) {
                setFilterChoose(
                    filterChoose.filter(
                        (f) => typeof f !== "string" || !others.includes(f)
                    )
                );
                return;
            }
            setDate_selected(new Date())
            setHour_selected('12h30')
        }
        else if(filterChoose.includes('Ouvert ce soir')) {
            const others = openedFilters.filter(f => f !== 'Ouvert ce soir');
            if (others.some(f => filterChoose.includes(f))) {
                setFilterChoose(
                    filterChoose.filter(
                        (f) => typeof f !== "string" || !others.includes(f)
                    )
                );
                return;
            }
            setDate_selected(new Date())
            setHour_selected('20h00') 
        }
        else if(filterChoose.includes('Ouvert demain midi')) {
            const others = openedFilters.filter(f => f !== 'Ouvert demain midi');
            if (others.some(f => filterChoose.includes(f))) {
                setFilterChoose(filterChoose.filter((f) => typeof f !== "string" || !others.includes(f)));
                return;
            }
        }
        else if(filterChoose.includes('Ouvert demain soir')) {
            const others = openedFilters.filter(f => f !== 'Ouvert demain soir');
            if (others.some(f => filterChoose.includes(f))) {
                setFilterChoose(filterChoose.filter((f) => typeof f !== "string" || !others.includes(f)));
                return;
            }
        }
        else if(filterChoose.includes('Ouvert maintenant')) {
            const others = openedFilters.filter(f => f !== 'Ouvert maintenant');
            if (others.some(f => filterChoose.includes(f))) {
                setFilterChoose(filterChoose.filter((f) => typeof f !== "string" || !others.includes(f)));
                return;
            }
        }
        const filteredList = changementSchedule_Date();


        startTransition(() => {
            setListRestoFiltered(filteredList);
            setListRestoBackup(filteredList);
        })  
    }, [filterChoose]); 

    useEffect(() => {
        const filteredList = changementSchedule_Date()
        startTransition(() => {
            setListRestoFiltered(filteredList);
            setListRestoBackup(filteredList);
        })
        const viewResto = document.getElementById('mainCorpRestau');
        viewResto?.scrollTo(0, 0);
    },[listResto]) 

    useEffect(() => {
        const list = changementSchedule_Date()
        startTransition(() => {
            setListRestoFiltered(list); 
            setListRestoBackup(list);
        })
    },[hour_selected,date_selected])  


    const priceFilter = useCallback((min:number,max:number) => {
        startTransition(() => {
            setFilterChoose((prev) => {
                let updatedFilters = [...prev];
                const index = updatedFilters.findIndex(filter => 
                    Array.isArray(filter) && filter.length === 2 && 
                    filter.every(num => typeof num === "number")
                );
                if (index !== -1) updatedFilters[index] = [min, max];
                else updatedFilters.push([min, max]);
                return updatedFilters
            })
        })
    },[setFilterChoose])

    const fetchRestaurants = useCallback(async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_DIRECTION_REQUEST}/api/getResto`, { 
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const data: Restaurant[] = await response.json();
            startTransition(() => {
                setListResto(data);
                setListRestoHome(data);
                setListRestoAll(data); 
                setFetchResto(true)
            })
        } catch (error) {
            console.error("Error fetching restaurant data:", error);
            setFetchResto(true)
        }
    },[]);

    useEffect(() => {
        const data = listResto;
        startTransition(() => {
            setListRestoAll(data); 
        });
    }, [listResto]);

    const resetCityRestaurants = useCallback(() => {
        setListResto(listRestoHome);
    }, [listRestoHome]);


  const value = useMemo(
    () => ({
        priceFilter,
        fetchRestaurants,
        fetchResto,setFetchResto,
        listResto,setListResto,
        listRestoHome,setListRestoHome,
        listRestoAll,setListRestoAll,
        listRestoFiltered,setListRestoFiltered,
        listRestoBackup,setListRestoBackup,
        miniRestos,setMiniRestos,
        miniRestosLoaded,setMiniRestosLoaded,
        fetchMiniRestos,
        resetCityRestaurants
    }),
    [
        priceFilter,
        fetchRestaurants,
        fetchResto,
        listResto,
        listRestoHome,
        listRestoAll,
        listRestoFiltered,
        listRestoBackup,
        miniRestos,
        miniRestosLoaded,
        fetchMiniRestos,
        resetCityRestaurants
    ]
  );

  return <RestaurantContext.Provider value={value}>{children}</RestaurantContext.Provider>;
};

export { RestaurantContext };
